/**
 * Work Creation Wizard Component
 * Multi-step wizard for creating planned works with task assignment and work packages
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Autocomplete,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Alert,
  Stack,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Assignment as TaskIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Group as TeamIcon,
  Settings as EquipmentIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { format } from 'date-fns';

import { useSiteContext } from '../../hooks/useSiteContext';
import plannedWorksService from '../../services/plannedWorksService';
import {
  WorkType,
  PlannedWorkDetail,
  WorkTodoItem,
  CreatePlannedWorkRequest,
  CreateWorkTodoItemRequest,
  User,
  Equipment
} from '../../types/plannedWorks';

interface WorkCreationWizardProps {
  open: boolean;
  onClose: () => void;
  onWorkCreated: (work: PlannedWorkDetail) => void;
  existingPackageId?: number;
}

interface WorkFormData {
  title: string;
  description: string;
  work_type_id: number | null;
  equipment_id: number | null;
  priority: 'low' | 'medium' | 'high' | 'critical';
  assigned_to_id: number | null;
  proposed_start_date: Date | null;
  estimated_duration_hours: number;
  requires_permit: boolean;
  requires_isolation: boolean;
  requires_ppe: boolean;
  hot_work: boolean;
  confined_space: boolean;
  work_at_height: boolean;
  special_instructions: string;
  todos: CreateWorkTodoItemRequest[];
}

interface TodoFormData {
  title: string;
  description: string;
  estimated_duration_minutes: number;
  assigned_to_id: number | null;
  requires_safety_check: boolean;
  depends_on: number | null;
}

const WORK_PRIORITIES = [
  { value: 'low', label: 'Low', color: 'success' as const },
  { value: 'medium', label: 'Medium', color: 'warning' as const },
  { value: 'high', label: 'High', color: 'error' as const },
  { value: 'critical', label: 'Critical', color: 'error' as const }
];

const STEPS = ['Work Details', 'Safety & Requirements', 'Task Breakdown', 'Assignment & Scheduling', 'Review'];

export const WorkCreationWizard: React.FC<WorkCreationWizardProps> = ({
  open,
  onClose,
  onWorkCreated,
  existingPackageId
}) => {
  const { currentSite } = useSiteContext();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form data
  const [formData, setFormData] = useState<WorkFormData>({
    title: '',
    description: '',
    work_type_id: null,
    equipment_id: null,
    priority: 'medium',
    assigned_to_id: null,
    proposed_start_date: null,
    estimated_duration_hours: 4,
    requires_permit: false,
    requires_isolation: false,
    requires_ppe: true,
    hot_work: false,
    confined_space: false,
    work_at_height: false,
    special_instructions: '',
    todos: []
  });

  // Reference data
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  
  // Todo creation
  const [newTodo, setNewTodo] = useState<TodoFormData>({
    title: '',
    description: '',
    estimated_duration_minutes: 30,
    assigned_to_id: null,
    requires_safety_check: false,
    depends_on: null
  });

  useEffect(() => {
    if (open) {
      fetchReferenceData();
    }
  }, [open]);

  const fetchReferenceData = async () => {
    try {
      const [workTypesRes, equipmentRes, usersRes] = await Promise.all([
        plannedWorksService.workTypes.getAll(),
        fetch('/api/v1/sites/equipment/').then(r => r.json()),
        fetch('/api/v1/users/site-users/').then(r => r.json())
      ]);
      
      setWorkTypes(Array.isArray(workTypesRes) ? workTypesRes : []);
      setEquipment(Array.isArray(equipmentRes.results) ? equipmentRes.results : Array.isArray(equipmentRes) ? equipmentRes : []);
      setUsers(Array.isArray(usersRes.results) ? usersRes.results : Array.isArray(usersRes) ? usersRes : []);
    } catch (err) {
      console.error('Failed to fetch reference data:', err);
      setError('Failed to load reference data');
    }
  };

  const handleWorkTypeChange = (workTypeId: number) => {
    const workType = Array.isArray(workTypes) ? workTypes.find(wt => wt.id === workTypeId) : null;
    if (workType) {
      setFormData(prev => ({
        ...prev,
        work_type_id: workTypeId,
        estimated_duration_hours: workType.default_duration_hours || 4,
        requires_permit: workType.default_permit_required || false,
        requires_isolation: workType.default_isolation_required || false,
        hot_work: workType.default_hot_work || false
      }));
    }
  };

  const addTodo = () => {
    if (newTodo.title.trim()) {
      const todo: CreateWorkTodoItemRequest = {
        ...newTodo,
        order: formData.todos.length + 1
      };
      
      setFormData(prev => ({
        ...prev,
        todos: [...prev.todos, todo]
      }));
      
      setNewTodo({
        title: '',
        description: '',
        estimated_duration_minutes: 30,
        assigned_to_id: null,
        requires_safety_check: false,
        depends_on: null
      });
    }
  };

  const removeTodo = (index: number) => {
    setFormData(prev => ({
      ...prev,
      todos: prev.todos.filter((_, i) => i !== index)
    }));
  };

  const moveTodo = (index: number, direction: 'up' | 'down') => {
    const newTodos = [...formData.todos];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex >= 0 && targetIndex < newTodos.length) {
      [newTodos[index], newTodos[targetIndex]] = [newTodos[targetIndex], newTodos[index]];
      
      // Update order values
      newTodos.forEach((todo, i) => {
        todo.order = i + 1;
      });
      
      setFormData(prev => ({ ...prev, todos: newTodos }));
    }
  };

  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!currentSite?.siteId) {
        throw new Error('No site selected');
      }

      const workRequest: CreatePlannedWorkRequest = {
        ...formData,
        site: currentSite.siteId,
        work_package: existingPackageId || undefined
      };

      const createdWork = await plannedWorksService.plannedWorks.create(workRequest);
      onWorkCreated(createdWork);
      onClose();
    } catch (err) {
      console.error('Failed to create work:', err);
      setError(err instanceof Error ? err.message : 'Failed to create work');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Work Details
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Work Title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Work Type</InputLabel>
                <Select
                  value={formData.work_type_id || ''}
                  onChange={(e) => handleWorkTypeChange(Number(e.target.value))}
                  required
                >
                  {workTypes.map(type => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name} - {type.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
                >
                  {WORK_PRIORITIES.map(priority => (
                    <MenuItem key={priority.value} value={priority.value}>
                      <Chip label={priority.label} color={priority.color} size="small" />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Autocomplete
                options={Array.isArray(equipment) ? equipment : []}
                getOptionLabel={(option) => `${option.name} (${option.kks_code})`}
                value={Array.isArray(equipment) ? equipment.find(eq => eq.id === formData.equipment_id) || null : null}
                onChange={(_, value) => setFormData(prev => ({ ...prev, equipment_id: value?.id || null }))}
                renderInput={(params) => (
                  <TextField {...params} label="Equipment (Optional)" />
                )}
              />
            </Grid>
          </Grid>
        );

      case 1: // Safety & Requirements
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Safety Requirements
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.requires_permit}
                      onChange={(e) => setFormData(prev => ({ ...prev, requires_permit: e.target.checked }))}
                    />
                  }
                  label="Requires Permit"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.requires_isolation}
                      onChange={(e) => setFormData(prev => ({ ...prev, requires_isolation: e.target.checked }))}
                    />
                  }
                  label="Requires Isolation"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.requires_ppe}
                      onChange={(e) => setFormData(prev => ({ ...prev, requires_ppe: e.target.checked }))}
                    />
                  }
                  label="Requires PPE"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.hot_work}
                      onChange={(e) => setFormData(prev => ({ ...prev, hot_work: e.target.checked }))}
                    />
                  }
                  label="Hot Work"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.confined_space}
                      onChange={(e) => setFormData(prev => ({ ...prev, confined_space: e.target.checked }))}
                    />
                  }
                  label="Confined Space"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.work_at_height}
                      onChange={(e) => setFormData(prev => ({ ...prev, work_at_height: e.target.checked }))}
                    />
                  }
                  label="Work at Height"
                />
              </Grid>
            </Grid>
            
            <Box mt={3}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Special Instructions"
                value={formData.special_instructions}
                onChange={(e) => setFormData(prev => ({ ...prev, special_instructions: e.target.value }))}
                placeholder="Any special safety considerations or instructions..."
              />
            </Box>
          </Box>
        );

      case 2: // Task Breakdown
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Task Breakdown
            </Typography>
            
            {/* Add New Task */}
            <Card sx={{ mb: 3, border: '2px dashed #ccc' }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Add New Task
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Task Title"
                      value={newTodo.title}
                      onChange={(e) => setNewTodo(prev => ({ ...prev, title: e.target.value }))}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Duration (minutes)"
                      value={newTodo.estimated_duration_minutes}
                      onChange={(e) => setNewTodo(prev => ({ ...prev, estimated_duration_minutes: Number(e.target.value) }))}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Task Description"
                      value={newTodo.description}
                      onChange={(e) => setNewTodo(prev => ({ ...prev, description: e.target.value }))}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box display="flex" gap={2} alignItems="center">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={newTodo.requires_safety_check}
                            onChange={(e) => setNewTodo(prev => ({ ...prev, requires_safety_check: e.target.checked }))}
                          />
                        }
                        label="Safety Check Required"
                      />
                      
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={addTodo}
                        disabled={!newTodo.title.trim()}
                      >
                        Add Task
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            
            {/* Task List */}
            {formData.todos.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Tasks ({formData.todos.length})
                  </Typography>
                  
                  <List>
                    {formData.todos.map((todo, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemIcon>
                            <TaskIcon />
                          </ListItemIcon>
                          
                          <ListItemText
                            primary={todo.title}
                            secondary={
                              <Box>
                                <Typography variant="body2">
                                  {todo.description}
                                </Typography>
                                <Box display="flex" gap={1} mt={1}>
                                  <Chip size="small" label={`${todo.estimated_duration_minutes} min`} />
                                  {todo.requires_safety_check && (
                                    <Chip size="small" color="warning" label="Safety Check" />
                                  )}
                                </Box>
                              </Box>
                            }
                          />
                          
                          <ListItemSecondaryAction>
                            <IconButton
                              onClick={() => moveTodo(index, 'up')}
                              disabled={index === 0}
                              size="small"
                            >
                              ↑
                            </IconButton>
                            <IconButton
                              onClick={() => moveTodo(index, 'down')}
                              disabled={index === formData.todos.length - 1}
                              size="small"
                            >
                              ↓
                            </IconButton>
                            <IconButton
                              onClick={() => removeTodo(index)}
                              color="error"
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < formData.todos.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}
            
            {formData.todos.length === 0 && (
              <Alert severity="info">
                Add tasks to break down the work into manageable steps. This helps with progress tracking and assignment.
              </Alert>
            )}
          </Box>
        );

      case 3: // Assignment & Scheduling
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={Array.isArray(users) ? users : []}
                getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.username})`}
                value={Array.isArray(users) ? users.find(user => user.id === formData.assigned_to_id) || null : null}
                onChange={(_, value) => setFormData(prev => ({ ...prev, assigned_to_id: value?.id || null }))}
                renderInput={(params) => (
                  <TextField {...params} label="Assigned To" />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Avatar sx={{ mr: 2 }}>
                      {option.first_name[0]}{option.last_name[0]}
                    </Avatar>
                    <Box>
                      <Typography>{option.first_name} {option.last_name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {option.role} • {option.department}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Estimated Duration (hours)"
                value={formData.estimated_duration_hours}
                onChange={(e) => setFormData(prev => ({ ...prev, estimated_duration_hours: Number(e.target.value) }))}
                inputProps={{ min: 0.5, step: 0.5 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <DateTimePicker
                label="Proposed Start Date"
                value={formData.proposed_start_date}
                onChange={(date) => setFormData(prev => ({ ...prev, proposed_start_date: date }))}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: "Leave blank to propose later"
                  }
                }}
              />
            </Grid>
            
            {existingPackageId && (
              <Grid item xs={12}>
                <Alert severity="info" icon={<TeamIcon />}>
                  This work will be added to the selected work package for coordinated execution.
                </Alert>
              </Grid>
            )}
          </Grid>
        );

      case 4: // Review
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Work Details
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">{formData.title}</Typography>
                    <Typography color="textSecondary" paragraph>
                      {formData.description}
                    </Typography>
                    
                    <Box display="flex" gap={1} mb={2}>
                      <Chip
                        label={WORK_PRIORITIES.find(p => p.value === formData.priority)?.label}
                        color={WORK_PRIORITIES.find(p => p.value === formData.priority)?.color}
                      />
                      {formData.work_type_id && (
                        <Chip
                          label={Array.isArray(workTypes) ? workTypes.find(wt => wt.id === formData.work_type_id)?.name : ''}
                          variant="outlined"
                        />
                      )}
                    </Box>
                    
                    <Typography variant="subtitle2" gutterBottom>
                      Duration: {formData.estimated_duration_hours} hours
                    </Typography>
                    
                    {formData.assigned_to_id && (
                      <Typography variant="subtitle2" gutterBottom>
                        Assigned to: {Array.isArray(users) ? users.find(u => u.id === formData.assigned_to_id)?.first_name : ''} {Array.isArray(users) ? users.find(u => u.id === formData.assigned_to_id)?.last_name : ''}
                      </Typography>
                    )}
                    
                    {formData.proposed_start_date && (
                      <Typography variant="subtitle2" gutterBottom>
                        Proposed start: {format(formData.proposed_start_date, 'PPpp')}
                      </Typography>
                    )}
                    
                    <Box mt={2}>
                      <Typography variant="subtitle2" gutterBottom>
                        Safety Requirements:
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        {formData.requires_permit && <Chip size="small" label="Permit Required" color="warning" />}
                        {formData.requires_isolation && <Chip size="small" label="Isolation Required" color="error" />}
                        {formData.requires_ppe && <Chip size="small" label="PPE Required" />}
                        {formData.hot_work && <Chip size="small" label="Hot Work" color="error" />}
                        {formData.confined_space && <Chip size="small" label="Confined Space" color="warning" />}
                        {formData.work_at_height && <Chip size="small" label="Work at Height" color="warning" />}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {formData.todos.length > 0 && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Tasks ({formData.todos.length})
                      </Typography>
                      
                      {formData.todos.map((todo, index) => (
                        <Box key={index} display="flex" alignItems="center" gap={1} mb={1}>
                          <CheckIcon color="disabled" fontSize="small" />
                          <Typography variant="body2">
                            {todo.title} ({todo.estimated_duration_minutes} min)
                          </Typography>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        {existingPackageId ? 'Add Work to Package' : 'Create New Planned Work'}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ width: '100%', mt: 2 }}>
          <Stepper activeStep={activeStep}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          <Box sx={{ mt: 3, mb: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {renderStepContent()}
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
        >
          Back
        </Button>
        
        {activeStep === STEPS.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !formData.title || !formData.work_type_id}
          >
            {loading ? 'Creating...' : 'Create Work'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              (activeStep === 0 && (!formData.title || !formData.work_type_id)) ||
              loading
            }
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WorkCreationWizard;
